import React, { useState } from 'react';
import { useProjectRoles, usePermissions, useSaveRole, useDeleteRole, Role, Permission, TagRestriction, PermissionPayload } from '../../hooks/useProjectRoles';
import { TagBoolean, TagBooleanValue } from '../../components/TagBoolean';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';

// ─── Accordion role editor ────────────────────────────────────────────────────

function RoleEditor({
  role,
  permissions,
  onSave,
  onRemove,
  saving,
}: {
  role: Role;
  permissions: Permission[];
  onSave: (updated: { id: string; name: string; permissions: PermissionPayload[]; tagRestriction: TagRestriction }) => void;
  onRemove: (id: string) => void;
  saving: boolean;
}) {
  const [name, setName] = useState(role.name);
  const [checked, setChecked] = useState<Set<string>>(
    new Set(role.permissions.map(p => p.name))
  );
  const [tagRestriction, setTagRestriction] = useState<TagBooleanValue>({
    and: role.tagRestriction?.and ?? [],
    or:  role.tagRestriction?.or  ?? [],
    not: role.tagRestriction?.not ?? [],
  });

  const toggle = (permName: string) =>
    setChecked(prev => {
      const next = new Set(prev);
      next.has(permName) ? next.delete(permName) : next.add(permName);
      return next;
    });

  const categories = Array.from(new Set(permissions.map(p => p.category)));

  return (
    <div>
      <div className="row">
        <div className="col-md-5">

          <div className="row">
            <div className="col-md-12">
              <input
                type="text"
                className="form-control"
                data-testid="admin-roles-edit-name-input"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
          <br />

          {categories.map(category => (
            <div className="row" key={category}>
              <div className="col-md-12">
                <h5>{category}</h5>
                <div className="well well-small">
                  {permissions.filter(p => p.category === category).map(perm => (
                    <div className="row" key={perm.name}>
                      <div className="col-md-11">{perm.title}</div>
                      <div className="col-md-1">
                        <div className="checkbox checkbox-primary" style={{ marginTop: 0 }}>
                          <input
                            id={`${perm.name}-${role.id}`}
                            type="checkbox"
                            data-testid="admin-roles-permission-checkbox"
                            checked={checked.has(perm.name)}
                            onChange={() => toggle(perm.name)}
                            disabled={saving}
                          />
                          <label htmlFor={`${perm.name}-${role.id}`} style={{ marginTop: -13 }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}

        </div>

        <div className="col-md-7" style={{ marginTop: 52 }}>
          <h5>Tag Restrictions</h5>
          <TagBoolean
            projectId={role.projectId}
            value={tagRestriction}
            onChange={setTagRestriction}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-12 text-right">
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !name.trim()}
            data-testid="admin-roles-save-btn"
            onClick={() => onSave({
              id: role.id,
              name: name.trim(),
              permissions: permissions
                .filter(p => checked.has(p.name))
                .map(p => ({ ...p, checked: true })),
              tagRestriction: {
                and: tagRestriction.and.map(t => t.id),
                or:  tagRestriction.or.map(t => t.id),
                not: tagRestriction.not.map(t => t.id),
              },
            })}
          >
            Save
          </button>
          {' '}
          <button
            type="button"
            className="btn btn-danger"
            disabled={saving}
            onClick={() => onRemove(role.id)}
            data-testid="admin-roles-remove-btn"
          >
            <i className="fas fa-trash" aria-hidden="true" /> Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export function RolesTab({ projectId }: { projectId: string }) {
  const { data: roles = [], isLoading } = useProjectRoles(projectId);
  const { data: permissions = [] } = usePermissions(projectId);
  const saveRole = useSaveRole(projectId);
  const deleteRole = useDeleteRole(projectId);

  const [newRoleName, setNewRoleName] = useState('');
  const [openRoleId, setOpenRoleId] = useState<string | null>(null);

  const handleAddRole = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoleName.trim()) return;
    saveRole.mutate({ name: newRoleName.trim(), permissions: [] }, {
      onSuccess: (created: any) => {
        setNewRoleName('');
        setOpenRoleId(created?.id ?? null);
      },
    });
  };

  const handleSave = (updated: { id: string; name: string; permissions: PermissionPayload[]; tagRestriction: TagRestriction }) => {
    saveRole.mutate(updated, { onSuccess: () => setOpenRoleId(null) });
  };

  if (isLoading) return <LoadingPlaceholder />;

  return (
    <div>
      <h2><i className="fas fa-users-cog" aria-hidden="true" /> Manage Roles</h2>

      <form className="row" onSubmit={handleAddRole} noValidate>
        <div className="form-group col-md-12">
          <div className="row">
            <div className="col-md-4">
              <input
                type="text"
                placeholder="Role Name"
                data-testid="admin-roles-name-input"
                className="form-control"
                value={newRoleName}
                maxLength={255}
                disabled={saveRole.isPending}
                onChange={e => setNewRoleName(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saveRole.isPending || !newRoleName.trim()}
                data-testid="admin-roles-add-btn"
              >
                Add Role <i className="fas fa-plus-circle" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </form>

      <div className="row">
        <div className="col-md-12">
          {roles.filter(r => r.canEdit !== false).map(role => {
            const isOpen = openRoleId === role.id;
            return (
              <div key={role.id} className="panel panel-default" data-testid="admin-roles-role-panel">
                <div
                  className="panel-heading"
                  data-testid="admin-roles-role-heading"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setOpenRoleId(isOpen ? null : role.id)}
                >
                  <h4 className="panel-title">
                    <span className={`fa fa-chevron-${isOpen ? 'down' : 'right'}`} aria-hidden="true" />
                    {' '}{role.name}
                  </h4>
                </div>
                {isOpen && (
                  <div className="panel-body">
                    <RoleEditor
                      role={role}
                      permissions={permissions}
                      onSave={handleSave}
                      onRemove={id => deleteRole.mutate(id, { onSuccess: () => setOpenRoleId(null) })}
                      saving={saveRole.isPending || deleteRole.isPending}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
